import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  HQ_MAX_CONDITIONS_PER_ROUTE,
  HQ_MAX_ROUTES,
  HQ_OP_OPTIONS,
  HQ_SENTENCE_MAX,
  cleanHqRule,
  emptyHqRule,
  extractQuestions,
  hqSaveErrorMessage,
  hqSuggestErrorMessage,
  matchesHqRule,
  opTakesValues,
  validateHqRule,
  type HqCompareOp,
  type HqCondition,
  type HqRule,
  type HqSuggestion,
} from "../lib/hq-rule";

/**
 * "Zusätzlich HQ-Lead erfassen, wenn …" on the webhook source page
 * (TASK-234, prototype variant A of docs/discovery/hq-lead-routing/epic.md).
 * TASK-235: "In eigenen Worten" + "Regel vorschlagen lassen" fill the draft from
 * the AI proposal (never saving, never switching the rule on); its hints sit
 * above the routes.
 */

const input =
  "rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-[11px] text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-accent";
const smallButton =
  "rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent disabled:opacity-40";

const OTHER = "__other__";
const blankCondition = (): HqCondition => ({ path: "", op: "equals", values: [""] });

function ConditionRow({
  cond,
  questions,
  onChange,
  onRemove,
  label,
}: {
  cond: HqCondition;
  questions: ReturnType<typeof extractQuestions>;
  onChange: (next: HqCondition) => void;
  onRemove: () => void;
  /** Position for screen readers, e.g. "Weg 1, Bedingung 2" or "Ausschluss 1". */
  label: string;
}) {
  const known = questions.some((q) => q.path === cond.path);
  const [free, setFree] = useState(questions.length === 0 || (cond.path !== "" && !known));
  const values = cond.values ?? [];
  const takesValues = opTakesValues(cond.op);

  const setOp = (op: HqCompareOp) => {
    if (!opTakesValues(op)) {
      onChange({ path: cond.path, op });
    } else if (op === "equals") {
      onChange({ path: cond.path, op, values: [values[0] ?? ""] });
    } else {
      onChange({ path: cond.path, op, values: values.length > 0 ? values : [""] });
    }
  };
  // ponytail: several values are typed comma separated; a wanted value that
  // itself contains a comma can't be entered under "ist eines von"/"enthält".
  // The typed text is kept as is (a comma can be deleted again); the list is
  // only re-joined when the values changed from outside (Verwerfen, new op).
  const split = (raw: string) => raw.split(/,\s*/);
  const [raw, setRaw] = useState<string | null>(null);
  const listText =
    cond.op === "equals" ? (values[0] ?? "") : raw !== null && JSON.stringify(split(raw)) === JSON.stringify(values) ? raw : values.join(", ");
  const setValue = (text: string) => {
    setRaw(text);
    onChange({ ...cond, values: cond.op === "equals" ? [text] : split(text) });
  };

  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      {questions.length > 0 && (
        <select
          aria-label={`${label}: Frage`}
          value={free ? OTHER : cond.path}
          onChange={(e) => {
            if (e.target.value === OTHER) {
              setFree(true);
              onChange({ ...cond, path: "" });
            } else {
              setFree(false);
              onChange({ ...cond, path: e.target.value });
            }
          }}
          className={`${input} w-52 flex-shrink-0`}
        >
          <option value="">Frage wählen</option>
          {questions.map((q) => (
            <option key={q.path} value={q.path}>
              {q.label}
            </option>
          ))}
          <option value={OTHER}>Anderes Feld (Pfad) …</option>
        </select>
      )}
      {free && (
        <input
          type="text"
          aria-label={`${label}: Pfad`}
          value={cond.path}
          onChange={(e) => onChange({ ...cond, path: e.target.value })}
          placeholder="data.email"
          className={`${input} w-52 flex-shrink-0 font-mono`}
        />
      )}
      <select
        aria-label={`${label}: Vergleich`}
        value={cond.op}
        onChange={(e) => setOp(e.target.value as HqCompareOp)}
        className={`${input} w-36 flex-shrink-0`}
      >
        {HQ_OP_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {takesValues && (
        <input
          type="text"
          aria-label={`${label}: Wert`}
          value={listText}
          onChange={(e) => setValue(e.target.value)}
          placeholder={cond.op === "equals" ? "Wert" : "Werte, mit Komma getrennt"}
          className={`${input} min-w-40 flex-1`}
        />
      )}
      <button
        type="button"
        aria-label={`${label} entfernen`}
        title={`${label} entfernen`}
        onClick={onRemove}
        className="rounded-md p-1 text-text-tertiary hover:text-danger focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </li>
  );
}

export function HqRuleEditor({
  saved,
  samples,
  onSave,
  onSuggest,
  metaSummary,
  busy = false,
}: {
  /** The rule as stored; null = none yet. */
  saved: HqRule | null;
  samples: unknown[];
  /** Resolves when stored; rejects (ApiError) with the server's 400 message. */
  onSave: (rule: HqRule) => Promise<void>;
  /** Asks the AI for a proposal from one sentence; rejects (ApiError) with the server's message (e.g. 503). */
  onSuggest: (sentence: string) => Promise<HqSuggestion>;
  /** What the Meldungs-Tabelle sends to Meta, in one sentence. */
  metaSummary: string;
  /** Another save or a reload of the stored mapping is running. */
  busy?: boolean;
}) {
  const base = saved ?? emptyHqRule();
  const savedKey = JSON.stringify(base);
  const [draft, setDraft] = useState<HqRule>(base);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sentence, setSentence] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  // New stored state (after saving, or another tab) replaces the draft.
  useEffect(() => {
    setDraft(base);
    setError(null);
    setNotes([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const questions = useMemo(() => extractQuestions(samples), [samples]);
  const cleaned = useMemo(() => cleanHqRule(draft), [draft]);
  const formError = validateHqRule(cleaned);
  const hqCount = useMemo(
    () =>
      formError
        ? 0
        : samples.filter((s) => matchesHqRule(s, { ...cleaned, enabled: true })).length,
    [samples, cleaned, formError],
  );
  const dirty = JSON.stringify(draft) !== savedKey;
  const exclusions = draft.exclusions ?? [];

  // Rows keep local UI state (free path, typed text); after a removal every
  // row is rebuilt from its own data so no row inherits a removed row's state.
  const [rev, setRev] = useState(0);
  const setRoutes = (routes: HqCondition[][]) => setDraft({ ...draft, routes });
  const removeCondition = (r: number, c: number) => {
    setRev((n) => n + 1);
    setRoutes(draft.routes.map((route, ri) => (ri === r ? route.filter((_, j) => j !== c) : route)).filter((route) => route.length > 0));
  };
  // Off = key absent (the server default), so switching on and off again is no change.
  const setMetaHqOnly = (on: boolean) => {
    const { meta_hq_only: _drop, ...rest } = draft;
    setDraft(on ? { ...rest, meta_hq_only: true } : rest);
  };
  const setExclusions = (next: HqCondition[]) => setDraft({ ...draft, exclusions: next });
  const setCondition = (r: number, c: number, next: HqCondition) =>
    setRoutes(draft.routes.map((route, ri) => (ri === r ? route.map((x, ci) => (ci === c ? next : x)) : route)));

  const suggest = async () => {
    setSuggesting(true);
    setSuggestError(null);
    try {
      const res = await onSuggest(sentence.trim());
      // Routes and exclusions only: switching the rule on changes what goes to
      // Meta and stays the operator's own click. Nothing is saved here. An
      // answer without any route (nothing mappable) keeps the draft and only
      // shows the hints.
      if (res.hq_rule.routes.length > 0) {
        setRev((n) => n + 1);
        // Functional update: the draft as it is now, not as it was at the click.
        setDraft((d) => ({ ...d, routes: res.hq_rule.routes, exclusions: res.hq_rule.exclusions ?? [] }));
      }
      setNotes(res.notes);
    } catch (e) {
      setSuggestError(hqSuggestErrorMessage(e));
    } finally {
      setSuggesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(cleanHqRule(draft));
    } catch (e) {
      setError(hqSaveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-labelledby="hq-rule-title"
      className="rounded-md border border-border-primary bg-bg-secondary p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="hq-rule-title" className="text-sm font-medium text-text-primary">
            Zusätzlich HQ-Lead erfassen, wenn …
          </h2>
          <p className="mt-1 text-xs text-text-secondary">
            Jede Eintragung wird weiter als Lead erfasst. Stimmt einer der Wege und kein Ausschluss, kommt ein
            HQ-Lead für dieselbe Person dazu.
          </p>
        </div>
        <label className="flex flex-shrink-0 items-center gap-2 text-xs text-text-secondary">
          <input
            type="checkbox"
            role="switch"
            aria-label="HQ-Regel aktiv"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            className="h-4 w-4 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          />
          {draft.enabled ? "an" : "aus"}
        </label>
      </div>

      <div className="mt-3 rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2">
        <label
          className={`flex items-center gap-2 text-xs font-medium ${
            draft.enabled ? "text-text-primary" : "text-text-tertiary"
          }`}
        >
          <input
            type="checkbox"
            role="switch"
            aria-label="An Meta nur HQ-Leads als Lead melden"
            checked={draft.meta_hq_only === true}
            disabled={!draft.enabled}
            onChange={(e) => setMetaHqOnly(e.target.checked)}
            className="h-4 w-4 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          />
          An Meta nur HQ-Leads als Lead melden
        </label>
        <p className="mt-1 pl-6 text-[11px] text-text-secondary">
          {draft.enabled
            ? "Normale Leads dieser Quelle gehen nicht an Meta; HQ-Leads gehen als Lead, wenn Lead in der Meldungs-Tabelle angehakt ist."
            : "Wirkt nur, solange die HQ-Regel eingeschaltet ist."}
        </p>
      </div>

      <div className="mt-4 rounded-md border border-border-primary bg-bg-primary p-3">
        <label htmlFor="hq-sentence" className="text-xs font-medium text-text-primary">
          In eigenen Worten
        </label>
        <p className="mt-0.5 text-[11px] text-text-tertiary">
          Zum Beispiel: „HQ = ab 10 Mitarbeitern, nie wenn nur Content“. Die KI baut daraus einen Vorschlag
          für die Wege unten; gespeichert wird erst mit „Regel speichern“.
        </p>
        <textarea
          id="hq-sentence"
          aria-label="In eigenen Worten"
          value={sentence}
          maxLength={HQ_SENTENCE_MAX}
          rows={2}
          onChange={(e) => setSentence(e.target.value)}
          className={`${input} mt-2 w-full resize-y`}
        />
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            aria-busy={suggesting}
            disabled={suggesting || !sentence.trim()}
            onClick={suggest}
            className={smallButton}
          >
            Regel vorschlagen lassen
          </button>
          {suggesting && (
            <span role="status" className="text-[11px] text-text-secondary">
              Vorschlag wird erstellt …
            </span>
          )}
          <span className="ml-auto font-mono text-[10px] text-text-tertiary">
            {sentence.length} / {HQ_SENTENCE_MAX}
          </span>
        </div>
        {suggestError && (
          <p
            role="alert"
            className="mt-2 rounded-md border border-danger bg-danger-bg px-3 py-2 text-xs text-danger"
          >
            {suggestError}
          </p>
        )}
      </div>

      {notes.length > 0 && (
        <ul
          aria-label="Hinweise zum Vorschlag"
          className="mt-3 space-y-1 rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2 text-xs text-text-primary"
        >
          {notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-col gap-4 lg:flex-row">
        <div className={`min-w-0 flex-1 space-y-3 ${draft.enabled ? "" : "opacity-60"}`}>
          {draft.routes.map((route, ri) => (
            <div key={`${rev}-${ri}`} className="space-y-3">
              {ri > 0 && (
                <p className="text-center font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
                  ODER
                </p>
              )}
              <div className="rounded-md border border-border-primary bg-bg-primary p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-text-primary">
                    Weg {ri + 1}: alle Bedingungen müssen stimmen
                  </p>
                  <button
                    type="button"
                    aria-label={`Weg ${ri + 1} entfernen`}
                    onClick={() => {
                      setRev((n) => n + 1);
                      setRoutes(draft.routes.filter((_, i) => i !== ri));
                    }}
                    className={smallButton}
                  >
                    Weg entfernen
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {route.map((cond, ci) => (
                    <ConditionRow
                      key={`${rev}-${ci}`}
                      cond={cond}
                      questions={questions}
                      onChange={(next) => setCondition(ri, ci, next)}
                      onRemove={() => removeCondition(ri, ci)}
                      label={`Weg ${ri + 1}, Bedingung ${ci + 1}`}
                    />
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={route.length >= HQ_MAX_CONDITIONS_PER_ROUTE}
                  onClick={() =>
                    setRoutes(draft.routes.map((r, i) => (i === ri ? [...r, blankCondition()] : r)))
                  }
                  className={`${smallButton} mt-2`}
                >
                  + Bedingung
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            disabled={draft.routes.length >= HQ_MAX_ROUTES}
            onClick={() => setRoutes([...draft.routes, [blankCondition()]])}
            className={smallButton}
          >
            + Weg
          </button>

          <div className="rounded-md border border-border-primary bg-bg-primary p-3">
            <p className="mb-2 text-xs font-medium text-text-primary">
              Nie HQ, wenn eine dieser Bedingungen zutrifft
            </p>
            <ul className="space-y-1.5">
              {exclusions.map((cond, ei) => (
                <ConditionRow
                  key={`${rev}-${ei}`}
                  cond={cond}
                  questions={questions}
                  onChange={(next) => setExclusions(exclusions.map((x, i) => (i === ei ? next : x)))}
                  onRemove={() => {
                    setRev((n) => n + 1);
                    setExclusions(exclusions.filter((_, i) => i !== ei));
                  }}
                  label={`Ausschluss ${ei + 1}`}
                />
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setExclusions([...exclusions, blankCondition()])}
              className={`${smallButton} ${exclusions.length > 0 ? "mt-2" : ""}`}
            >
              + Ausschluss
            </button>
          </div>
        </div>

        <aside className="w-full flex-shrink-0 space-y-3 text-xs lg:w-64">
          <div className="rounded-md border border-border-secondary bg-bg-tertiary p-3">
            <p className="mb-1.5 font-medium text-text-primary">Fragen in den Proben</p>
            {samples.length === 0 ? (
              <p className="text-text-secondary">
                Noch keine Proben gespeichert. Sobald die Datenquelle Eintragungen empfangen hat, erscheinen
                die Fragen hier.
              </p>
            ) : questions.length === 0 ? (
              <p className="text-text-secondary">
                In den Proben steckt keine Formularfrage. Bedingungen gehen über „Anderes Feld (Pfad)“.
              </p>
            ) : (
              <ul className="space-y-1">
                {questions.map((q) => (
                  <li key={q.path} className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate text-text-primary" title={q.label}>
                      {q.label}
                    </span>
                    <span className="flex-shrink-0 font-mono text-[11px] text-text-secondary">
                      {q.answered} / {samples.length} beantwortet
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-md border border-border-secondary bg-bg-tertiary p-3">
            <p className="mb-1.5 font-medium text-text-primary">Was an Meta geht</p>
            <p className="text-text-secondary">{metaSummary}</p>
          </div>
        </aside>
      </div>

      {error && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-danger bg-danger-bg px-3 py-2 text-xs text-danger"
        >
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border-primary pt-3">
        <span aria-live="polite" className="flex flex-wrap items-center gap-3">
          {samples.length === 0 ? (
            <span className="text-[11px] text-text-tertiary">Ohne Proben keine Zählung.</span>
          ) : (
            <span
              className={`font-mono text-[11px] ${
                hqCount === 0 && draft.enabled && !formError && cleaned.routes.length > 0
                  ? "text-danger"
                  : "text-text-secondary"
              }`}
            >
              {hqCount} von {samples.length} Proben wären HQ
            </span>
          )}
          {samples.length > 0 && formError && (
            <span className="text-[11px] text-text-tertiary">Noch nicht vollständig: {formError}</span>
          )}
        </span>
        <span className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={(!dirty && notes.length === 0) || saving}
            onClick={() => {
              setDraft(base);
              setNotes([]);
            }}
            className={smallButton}
          >
            Verwerfen
          </button>
          <button
            type="button"
            disabled={!dirty || saving || busy}
            onClick={save}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-50"
          >
            Regel speichern
          </button>
        </span>
      </div>
    </section>
  );
}
